import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";
import PageSEO from "@/components/feature/PageSEO";

function FadeIn({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}>
      {children}
    </div>
  );
}

export default function ContactoPage() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const mensaje = (form.elements.namedItem("mensaje") as HTMLTextAreaElement)?.value || "";
    if (mensaje.length > 500) return;

    setStatus("sending");
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      if (typeof value === "string") data.append(key, value);
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7abj2ibdfubkqslais0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif" }}>
      <PageSEO
        title="Contacto Vaciado de Pisos Girona | GIROBUIT — Presupuesto Gratis"
        description="Contacta con GIROBUIT para tu vaciado de piso o local en Girona. Presupuesto sin compromiso en menos de 24h. Llama al 602 36 59 96 o escríbenos por WhatsApp."
        canonical="https://girobuit.com/contacto"
        pageType="ContactPage"
        breadcrumbs={[
          { name: "Inicio", url: "https://girobuit.com/" },
          { name: "Contacto", url: "https://girobuit.com/contacto" },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-36 md:pt-40 pb-12 bg-[#330000] text-center px-4">
        <nav className="text-xs text-red-300 mb-4">
          <Link to="/" className="hover:text-white cursor-pointer">Inicio</Link>
          <span className="mx-2">/</span>
          <span className="text-white">Contacto</span>
        </nav>
        <h1 className="text-4xl md:text-5xl font-900 text-white mb-4" style={{ fontWeight: 900 }}>
          Contacta con nosotros
        </h1>
        <p className="text-red-200 max-w-xl mx-auto text-base">
          Pide tu presupuesto sin compromiso para el{" "}
          <Link to="/servicios" className="text-[#FF6666] hover:text-white underline cursor-pointer font-600" style={{ fontWeight: 600 }}>
            vaciado de tu piso o local en Girona
          </Link>. Respondemos en menos de 24 horas.
        </p>
      </section>

      {/* Main Content */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5">
            {/* Left Info */}
            <div className="lg:col-span-2 bg-[#330000] px-4 sm:px-8 md:px-12 py-16">
              <FadeIn>
                <h2 className="text-2xl font-800 text-white mb-6" style={{ fontWeight: 800 }}>Información de contacto</h2>
                <div className="flex flex-col gap-5 mb-10">
                  {[
                    { icon: "ri-phone-fill", label: "Teléfono / WhatsApp", value: "602 36 59 96", href: "tel:+34602365996" },
                    { icon: "ri-mail-fill", label: "Email", value: "info@giornabuit.com", href: "mailto:info@giornabuit.com" },
                    { icon: "ri-map-pin-fill", label: "Zona de servicio", value: "Girona y comarca del Gironès", href: null },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#ff0000]/30 flex-shrink-0">
                        <i className={`${item.icon} text-[#FF6666]`}></i>
                      </div>
                      <div>
                        <div className="text-xs text-red-300 mb-0.5">{item.label}</div>
                        {item.href ? (
                          <a href={item.href} className="text-white font-600 hover:text-[#FF6666] transition-colors cursor-pointer" style={{ fontWeight: 600 }}>{item.value}</a>
                        ) : (
                          <span className="text-white font-600" style={{ fontWeight: 600 }}>{item.value}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mb-8">
                  <h3 className="text-sm font-700 text-[#FF6666] mb-3" style={{ fontWeight: 700 }}>Horario de atención</h3>
                  <div className="flex flex-col gap-2 text-sm text-red-200">
                    <div className="flex justify-between"><span>Lunes – Viernes</span><span className="text-white">8:00 – 19:00</span></div>
                    <div className="flex justify-between"><span>Sábado</span><span className="text-white">9:00 – 14:00</span></div>
                    <div className="flex justify-between"><span>Domingo</span><span className="text-red-400">Cerrado</span></div>
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-700 text-[#FF6666] mb-3" style={{ fontWeight: 700 }}>Área de servicio</h3>
                  <div className="flex flex-wrap gap-2">
                    {["Girona", "Salt", "Sarrià de Ter", "Quart", "Banyoles", "Bescanó", "Celrà", "Cassà de la Selva"].map((a) => (
                      <span key={a} className="text-xs px-3 py-1 rounded-full bg-white/10 text-red-200 border border-white/10">{a}</span>
                    ))}
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-3">
                  <a href="tel:+34602365996" className="flex items-center gap-2 bg-[#ff0000] text-white px-5 py-3 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer hover:bg-[#cc0000] transition-colors flex-1 justify-center sm:flex-none" style={{ fontWeight: 700 }}>
                    <i className="ri-phone-fill"></i> Llamar
                  </a>
                  <a href="https://wa.me/34602365996" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full text-sm font-700 whitespace-nowrap cursor-pointer hover:bg-[#1da851] transition-colors flex-1 justify-center sm:flex-none" style={{ fontWeight: 700 }}>
                    <i className="ri-whatsapp-line"></i> WhatsApp
                  </a>
                </div>
                <div className="mt-6 flex flex-col gap-2 text-xs text-red-300">
                  <Link to="/servicios" className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <i className="ri-arrow-right-s-line text-[#FF6666]"></i> Ver todos nuestros servicios de vaciado
                  </Link>
                  <Link to="/faq" className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <i className="ri-arrow-right-s-line text-[#FF6666]"></i> Preguntas frecuentes sobre vaciados
                  </Link>
                  <Link to="/empresa" className="hover:text-white transition-colors cursor-pointer flex items-center gap-1.5">
                    <i className="ri-arrow-right-s-line text-[#FF6666]"></i> Quiénes somos y cómo trabajamos
                  </Link>
                </div>
              </FadeIn>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3 px-4 sm:px-8 md:px-12 py-16">
              <FadeIn>
                <h2 className="text-2xl font-800 text-[#330000] mb-2" style={{ fontWeight: 800 }}>Solicita tu presupuesto</h2>
                <p className="text-gray-500 text-sm mb-8">Sin compromiso. Te respondemos en menos de 24 horas.</p>

                {status === "success" ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 mb-4">
                      <i className="ri-check-line text-3xl text-green-600"></i>
                    </div>
                    <h3 className="text-xl font-700 text-[#330000] mb-2" style={{ fontWeight: 700 }}>¡Mensaje enviado!</h3>
                    <p className="text-gray-500 text-sm">Te contactaremos en menos de 24 horas. También puedes llamarnos al <a href="tel:+34602365996" className="text-[#ff0000] font-600 cursor-pointer">602 36 59 96</a>.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-600 text-[#330000] mb-1.5" style={{ fontWeight: 600 }}>Nombre *</label>
                        <input name="nombre" type="text" required placeholder="Tu nombre" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#ff0000] transition-colors duration-200" />
                      </div>
                      <div>
                        <label className="block text-xs font-600 text-[#330000] mb-1.5" style={{ fontWeight: 600 }}>Teléfono *</label>
                        <input name="telefono" type="tel" required placeholder="Tu teléfono" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#ff0000] transition-colors duration-200" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-600 text-[#330000] mb-1.5" style={{ fontWeight: 600 }}>Email</label>
                      <input name="email" type="email" placeholder="tu@email.com" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#ff0000] transition-colors duration-200" />
                    </div>
                    <div>
                      <label className="block text-xs font-600 text-[#330000] mb-1.5" style={{ fontWeight: 600 }}>Servicio que necesitas</label>
                      <select name="servicio" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#ff0000] transition-colors duration-200 bg-white cursor-pointer">
                        <option value="">Selecciona un servicio</option>
                        <option>Vaciado de piso o casa</option>
                        <option>Vaciado de local comercial</option>
                        <option>Retirada de muebles y electrodomésticos</option>
                        <option>Limpieza por síndrome de Diógenes</option>
                        <option>Pintura final</option>

                        <option>Varios servicios</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-600 text-[#330000] mb-1.5" style={{ fontWeight: 600 }}>Población</label>
                      <select name="poblacion" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#ff0000] transition-colors duration-200 bg-white cursor-pointer">
                        <option value="">Selecciona tu población</option>
                        <option>Girona ciudad</option>
                        <option>Salt</option>
                        <option>Sarrià de Ter</option>
                        <option>Quart</option>
                        <option>Vilablareix</option>
                        <option>Fornells de la Selva</option>
                        <option>Bescanó</option>
                        <option>Celrà</option>
                        <option>Cassà de la Selva</option>
                        <option>Banyoles</option>
                        <option>Otra población</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-600 text-[#330000] mb-1.5" style={{ fontWeight: 600 }}>
                        Mensaje * <span className={`ml-1 ${charCount > 450 ? "text-[#ff0000]" : "text-gray-400"}`}>({charCount}/500)</span>
                      </label>
                      <textarea
                        name="mensaje"
                        required
                        rows={4}
                        maxLength={500}
                        placeholder="Describe brevemente lo que necesitas: tipo de inmueble, tamaño aproximado, situación..."
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#ff0000] transition-colors duration-200 resize-none"
                        onChange={(e) => setCharCount(e.target.value.length)}
                      />
                    </div>
                    <div className="flex items-start gap-3">
                      <input name="privacidad" type="checkbox" required id="privacidad" className="mt-0.5 cursor-pointer accent-[#ff0000]" />
                      <label htmlFor="privacidad" className="text-xs text-gray-500 cursor-pointer leading-relaxed">
                        He leído y acepto la <Link to="/privacidad" className="text-[#ff0000] underline">política de privacidad</Link>. Mis datos serán usados únicamente para responder a mi consulta.
                      </label>
                    </div>
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="w-full bg-[#ff0000] text-white py-4 rounded-lg text-sm font-700 whitespace-nowrap cursor-pointer transition-all duration-300 hover:bg-[#cc0000] disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ fontWeight: 700 }}
                    >
                      {status === "sending" ? (
                        <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i> Enviando...</span>
                      ) : (
                        <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-fill"></i> Enviar solicitud de presupuesto</span>
                      )}
                    </button>
                    {status === "error" && (
                      <p className="text-xs text-red-600 text-center">Ha habido un error. Por favor, llámanos al 602 36 59 96.</p>
                    )}
                  </form>
                )}
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[400px] w-full">
        <iframe
          title="GIROBUIT Girona mapa"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d47498.12!2d2.8214!3d41.9794!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12bae72b0b7d9c3b%3A0x400c7c3a5e3d0!2sGirona!5e0!3m2!1ses!2ses!4v1700000000000"
          width="100%"
          height="400"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}
